import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, Sparkles, Brain, ArrowRight, X } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

interface OnboardingWelcomeProps {
  open: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

const steps = [
  {
    icon: Upload,
    titleKey: "onboarding.step1Title",
    titleFallback: "Upload your course material",
    descKey: "onboarding.step1Desc",
    descFallback: "Drop a PDF, paste your notes, or link a YouTube video. We support all formats.",
  },
  {
    icon: Sparkles,
    titleKey: "onboarding.step2Title",
    titleFallback: "We generate your revision sheet",
    descKey: "onboarding.step2Desc",
    descFallback: "Atlas structures your content into clear, concise notes optimized for memorization.",
  },
  {
    icon: Brain,
    titleKey: "onboarding.step3Title",
    titleFallback: "Practice, ask questions, master it",
    descKey: "onboarding.step3Desc",
    descFallback: "Take quizzes, ask questions about your content, and track your progress.",
  },
];

export function OnboardingWelcome({ open, onComplete, onSkip }: OnboardingWelcomeProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);

  const current = steps[step]!;
  const isLast = step === steps.length - 1;

  const handleNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setStep(step + 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onSkip()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center font-serif text-xl">
            {t("onboarding.welcome", "Welcome to System Atlas")}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 mb-2">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step ? "w-8 bg-primary" : i < step ? "w-4 bg-primary/40" : "w-4 bg-muted"
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
            className="flex flex-col items-center text-center py-6 px-4"
          >
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
              <current.icon className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              {t(current.titleKey, current.titleFallback)}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
              {t(current.descKey, current.descFallback)}
            </p>
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between pt-2">
          <Button variant="ghost" size="sm" onClick={onSkip} className="text-muted-foreground">
            {t("common.skip", "Skip")}
          </Button>
          <Button onClick={handleNext} className="gap-2">
            {isLast ? t("onboarding.getStarted", "Get started") : t("common.next", "Next")}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
